import { useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { Dialog } from './Dialog.js';
import { ThumbButton } from './ThumbButton.js';

const { t } = i18n;
interface Session {
  id: string;
  state: string;
  createdAt: string;
  startedById: string;
  startedByLabel: string;
}
interface Row {
  id: string;
  skuId: string;
  skuCode: string;
  skuName: string;
  skuType: string;
  photoUrl: string | null;
  locationLabel: string;
  observedQty: number;
  actualQty: number | null;
  state: string;
  version: number;
  stale: boolean;
}
type Detail = Session & { rows: Row[] };
const button =
  'rounded border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50';

export function StocktakeDialog({
  partnerId,
  onClose,
}: {
  partnerId: string;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const base = `/partners/${partnerId}/stocktakes`;
  const admin = user?.roles.some((r) => r.role === 'ADMIN') ?? false;
  const editable = detail?.state === 'DRAFT' && (admin || detail.startedById === user?.id);
  const active = sessions.some((s) => s.state === 'DRAFT' || s.state === 'SUBMITTED');
  const rows = detail?.rows.filter((r) => r.state !== 'REMOVED') ?? [];
  const complete =
    rows.length > 0 && rows.every((r) => !r.stale && ['MATCH', 'CORRECT'].includes(r.state));

  useEffect(() => {
    let cancelled = false;
    api<Session[]>(base)
      .then((data) => {
        if (!cancelled) setSessions(data);
      })
      .catch(() => {
        if (!cancelled) setError(t('common.error'));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [base]);

  async function load(id: string) {
    const data = await api<Detail>(`${base}/${id}`);
    setDetail(data);
    setCounts({});
    setSelected([]);
  }
  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await work();
      setSessions(await api<Session[]>(base));
    } catch {
      setError(t('stocktake.error'));
    } finally {
      setBusy(false);
    }
  }
  async function action(action: string) {
    if (!detail) return;
    const result = await api<{ recheck: boolean }>(`${base}/${detail.id}/action`, {
      method: 'POST',
      body: { action, ...(action === 'reopen' ? { skuIds: selected } : {}) },
    });
    await load(detail.id);
    if (result.recheck) setNotice(t('stocktake.recheck'));
  }
  async function count(row: Row, state: string) {
    const result = await api<{ recheck: boolean }>(`${base}/${detail!.id}/count`, {
      method: 'POST',
      body: {
        rowId: row.id,
        version: row.version,
        state,
        ...(state === 'CORRECT' ? { actualQty: Number(counts[row.id]) } : {}),
      },
    });
    // Preserve unsaved inputs in other rows after this row's explicit save.
    const data = await api<Detail>(`${base}/${detail!.id}`);
    setDetail(data);
    setCounts((old) => {
      if (result.recheck) return {};
      const next = { ...old };
      delete next[row.id];
      return next;
    });
    setNotice(t(result.recheck ? 'stocktake.recheck' : 'stocktake.saved'));
  }
  return (
    <Dialog title={t('stocktake.title')} onClose={onClose} size="wide">
      <div className="space-y-3 text-sm" aria-busy={busy}>
        <p>{t('stocktake.help')}</p>
        {error && (
          <p role="alert" className="text-red-600">
            {error}
          </p>
        )}
        {notice && <p role="status">{notice}</p>}
        {!detail ? (
          <>
            <button
              className={button}
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  setSessions(await api<Session[]>(base));
                })
              }
            >
              {t('stocktake.refresh')}
            </button>
            <button
              className={button}
              disabled={busy || active}
              onClick={() =>
                void run(async () => {
                  const created = await api<Session>(base, { method: 'POST', body: {} });
                  await load(created.id);
                })
              }
            >
              {t('stocktake.start')}
            </button>
            {active && <p>{t('stocktake.active')}</p>}
            {!busy && !sessions.length && <p>{t('stocktake.empty')}</p>}
            <ul className="space-y-2">
              {sessions.map((s) => (
                <li key={s.id}>
                  <button
                    className={button}
                    disabled={busy}
                    onClick={() => void run(() => load(s.id))}
                  >
                    {new Date(s.createdAt).toLocaleString(user?.locale ?? 'lt', {
                      timeZone: 'Europe/Vilnius',
                    })}{' '}
                    — {s.startedByLabel} — {t(`stocktake.state.${s.state}`)}
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <button
              className={button}
              disabled={busy}
              onClick={() => {
                setDetail(null);
                setCounts({});
              }}
            >
              {t('common.back')}
            </button>
            <p>
              {new Date(detail.createdAt).toLocaleString(user?.locale ?? 'lt', {
                timeZone: 'Europe/Vilnius',
              })}{' '}
              — {detail.startedByLabel} — {t(`stocktake.state.${detail.state}`)}
            </p>
            <button
              className={button}
              disabled={busy}
              onClick={() =>
                void run(() =>
                  ['DRAFT', 'SUBMITTED'].includes(detail.state) &&
                  (admin || detail.startedById === user?.id)
                    ? action('refresh')
                    : load(detail.id),
                )
              }
            >
              {t('stocktake.refresh')}
            </button>
            <ul className="space-y-2">
              {detail.rows.map((row) => (
                <li key={row.id} className="space-y-2 rounded border border-border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <ThumbButton url={row.photoUrl} alt={`${row.skuCode} — ${row.skuName}`} />
                    {admin && detail.state === 'SUBMITTED' && row.state !== 'REMOVED' && (
                      <input
                        type="checkbox"
                        aria-label={`${t('stocktake.select')} ${row.skuCode} ${row.locationLabel}`}
                        checked={selected.includes(row.skuId)}
                        disabled={busy}
                        onChange={(e) =>
                          setSelected((ids) =>
                            e.target.checked
                              ? [...new Set([...ids, row.skuId])]
                              : ids.filter((id) => id !== row.skuId),
                          )
                        }
                      />
                    )}
                    <strong>
                      {row.skuCode} — {row.skuName}
                    </strong>
                    <span>{t(`sku.type.${row.skuType}`)}</span>
                    <span>
                      {row.locationLabel === 'SHELF' ? t('stocktake.shelf') : row.locationLabel}
                    </span>
                    <span>
                      {t('stocktake.expected')}: {row.observedQty}
                    </span>
                    <span>
                      {t(`stocktake.row.${row.state}`)}
                      {row.actualQty !== null ? `: ${row.actualQty}` : ''}
                    </span>
                    {row.stale && <span className="text-red-600">{t('stocktake.changed')}</span>}
                  </div>
                  {editable && row.state !== 'REMOVED' && (
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        className={button}
                        disabled={busy || row.observedQty < 0 || row.observedQty > 1000000}
                        onClick={() => void run(() => count(row, 'MATCH'))}
                      >
                        {t('stocktake.match')}
                      </button>
                      <label>
                        {t('stocktake.actual')}{' '}
                        <input
                          type="number"
                          min="0"
                          max="1000000"
                          step="1"
                          className="w-28 rounded border border-border bg-background p-1"
                          disabled={busy}
                          value={counts[row.id] ?? ''}
                          onChange={(e) =>
                            setCounts((old) => ({ ...old, [row.id]: e.target.value }))
                          }
                        />
                      </label>
                      <button
                        className={button}
                        disabled={
                          busy ||
                          !counts[row.id]?.trim() ||
                          !Number.isInteger(Number(counts[row.id])) ||
                          Number(counts[row.id]) < 0 ||
                          Number(counts[row.id]) > 1000000
                        }
                        onClick={() => void run(() => count(row, 'CORRECT'))}
                      >
                        {t('common.save')}
                      </button>
                      <button
                        className={button}
                        disabled={busy}
                        onClick={() => void run(() => count(row, 'LATER'))}
                      >
                        {t('stocktake.later')}
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2">
              {editable && (
                <button
                  className={button}
                  disabled={
                    busy || !complete || Object.values(counts).some((value) => value.trim() !== '')
                  }
                  onClick={() => void run(() => action('submit'))}
                >
                  {t('stocktake.submit')}
                </button>
              )}
              {admin && detail.state === 'SUBMITTED' && (
                <>
                  <button
                    className={button}
                    disabled={busy || !complete}
                    onClick={() => {
                      if (window.confirm(t('stocktake.approve_confirm')))
                        void run(() => action('approve'));
                    }}
                  >
                    {t('stocktake.approve')}
                  </button>
                  <button
                    className={button}
                    disabled={busy}
                    onClick={() => void run(() => action('reopen'))}
                  >
                    {t('stocktake.reopen')}
                  </button>
                </>
              )}
              {admin && ['DRAFT', 'SUBMITTED'].includes(detail.state) && (
                <button
                  className={button}
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm(t('stocktake.reject_confirm')))
                      void run(() => action('reject'));
                  }}
                >
                  {t('stocktake.reject')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
